var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Menu, MenuContent, MenuItem, MenuList, Popper } from '@patternfly/react-core';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SuggestionContext } from '../providers';
import { applySuggestion } from '../utils/apply-suggestion';
import { getCursorWord } from '../utils/get-cursor-word';
export const useSuggestions = ({ propName, schema, inputRef, value, setValue }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [groupedSuggestions, setGroupedSuggestions] = useState({ root: [] });
    const menuRef = useRef(null);
    const firstElementRef = useRef(null);
    const { getProviders } = useContext(SuggestionContext);
    const suggestionProviders = useMemo(() => getProviders(propName, schema), [getProviders, propName, schema]);
    const onEscapeKey = useCallback((event) => {
        event.preventDefault();
        setIsVisible(false);
        requestAnimationFrame(() => {
            var _a;
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        });
    }, [inputRef]);
    const handleInputKeyDown = useCallback((event) => {
        if (!(event instanceof KeyboardEvent))
            return;
        if (event.ctrlKey && event.code === 'Space') {
            event.preventDefault();
            setIsVisible(true);
            requestAnimationFrame(() => {
                var _a, _b;
                (_a = firstElementRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                (_b = firstElementRef.current) === null || _b === void 0 ? void 0 : _b.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
            });
        }
        else if (event.key === 'Escape') {
            onEscapeKey(event);
        }
    }, [onEscapeKey]);
    const getHandleOnClick = useCallback((inputValue, suggestion) => () => {
        var _a, _b, _c;
        const { newValue, cursorPosition } = applySuggestion(suggestion, inputValue, (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart);
        setIsVisible(false);
        setValue === null || setValue === void 0 ? void 0 : setValue(newValue);
        (_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.focus();
        (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.setSelectionRange(cursorPosition, cursorPosition);
    }, [inputRef, setValue]);
    const getHandleMenuKeyDown = useCallback((inputValue, suggestion) => (event) => {
        if (event.key === 'Enter' && suggestion) {
            event.preventDefault();
            getHandleOnClick(inputValue, suggestion)();
        }
        else if (event.key === 'Escape') {
            onEscapeKey(event);
        }
    }, [getHandleOnClick, onEscapeKey]);
    useEffect(() => {
        let cancelled = false;
        if (!isVisible)
            return;
        const fetchSuggestions = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const cursorPosition = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.selectionStart;
            const { word } = getCursorWord(value, cursorPosition);
            const results = yield Promise.all(suggestionProviders.map((provider) => Promise.resolve(provider.getSuggestions(word, {
                propertyName: propName,
                inputValue: value,
                cursorPosition,
            }))));
            if (cancelled)
                return;
            const newGroupedSuggestions = results.flat().reduce((acc, suggestion) => {
                var _a, _b;
                const group = (_a = suggestion.group) !== null && _a !== void 0 ? _a : 'root';
                (_b = acc[group]) !== null && _b !== void 0 ? _b : (acc[group] = []);
                acc[group].push(suggestion);
                return acc;
            }, { root: [] });
            setGroupedSuggestions(newGroupedSuggestions);
        });
        fetchSuggestions();
        return () => {
            cancelled = true;
        };
    }, [suggestionProviders, value, propName, inputRef, isVisible]);
    useEffect(() => {
        const input = inputRef.current;
        if (!input)
            return;
        const handleFocus = () => {
            input.addEventListener('keydown', handleInputKeyDown);
        };
        const handleBlur = () => {
            input.removeEventListener('keydown', handleInputKeyDown);
        };
        input.addEventListener('focus', handleFocus);
        input.addEventListener('blur', handleBlur);
        if (document.activeElement === input) {
            handleFocus();
        }
        return () => {
            input.removeEventListener('focus', handleFocus);
            input.removeEventListener('blur', handleBlur);
            input.removeEventListener('keydown', handleInputKeyDown);
        };
    }, [handleInputKeyDown, inputRef]);
    return (_jsx(Popper, { isVisible: isVisible, triggerRef: inputRef, popperRef: menuRef, popper: _jsx("div", { ref: menuRef, children: _jsx(Menu, { containsFlyout: true, "data-testid": "suggestions-menu", children: _jsx(MenuContent, { children: _jsxs(MenuList, { children: [Object.entries(groupedSuggestions).map(([group, suggestions], groupIndex) => {
                                if (group === 'root') {
                                    return suggestions.map((suggestion, suggestionIndex) => {
                                        const isFirst = groupIndex === 0 && suggestionIndex === 0;
                                        return (_jsx(MenuItem, { ref: isFirst ? firstElementRef : null, onClick: getHandleOnClick(value, suggestion), onKeyDown: getHandleMenuKeyDown(value, suggestion), description: suggestion.description, children: suggestion.value }, suggestion.value));
                                    });
                                }
                                return (_jsx(MenuItem, { ref: groupIndex === 0 ? firstElementRef : null, flyoutMenu: _jsx(Menu, { containsFlyout: true, children: _jsx(MenuContent, { children: _jsx(MenuList, { children: suggestions.map((suggestion) => (_jsx(MenuItem, { onClick: getHandleOnClick(value, suggestion), onKeyDown: getHandleMenuKeyDown(value, suggestion), description: suggestion.description, children: suggestion.value }, suggestion.value))) }) }) }), children: _jsx("strong", { children: group }) }, group));
                            }), groupedSuggestions.root.length === 0 && _jsx(MenuItem, { isDisabled: true, children: "No suggestions available" })] }) }) }) }) }));
};
//# sourceMappingURL=suggestions.js.map